import {
    AppComponentProps,
    Nullable,
    OrgComponentProps,
    ReleaseComponentProps,
    ReleaseStepComponentProps,
} from '@/typescriptHelpers';
import {
    AppEntity,
    AppPlatform,
    AppSettings,
    CompletedReleaseSummary,
    GoogleBetaIntegrationData,
    IntegrationType,
    LifecycleStep,
    NextReleaseSummary,
    NonErrorRunwayResponseTypes,
    OrganizationEntity,
    Release,
    ReleaseStep,
    UpcomingReleaseSummary,
} from '@/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectApp,
    selectOrg,
    selectPaginationData,
    selectRelease,
    selectAppTimeFormatSetting,
    selectCadenceTimezone,
} from '@/selectors/selectors';
import { Updatable } from '@/actions/updatable';
import { FetchableEntityType, FetchableEntityTypeV2, FetchablePaginatedEntityType } from '@/entityType';
import {
    CommonSagaActionsData,
    PaginatedEntitiesRequest,
    StepConfigEntityRequest,
    StepRunEntityRequest,
    tryFetchEntity,
    TryFetchEntityActionData,
    tryFetchEntityV2,
    tryFetchPaginatedEntities,
    WorkflowEntityRequest,
    OrgEntityRequest,
} from '@/actions/entities';
import {
    automationIsCompleted,
    automationIsInactive,
    getError,
    getIsLoading,
    getReleaseStepFromState,
    getResponseType,
    getUpdated,
    getWillUpdate,
} from '@/helpers';
import {
    findLifecycleAutomationRun,
    getLifecycleStepCompleted,
    getLifecycleStepCompletedDate,
    getLifecycleStepDisplayText,
    getLifecycleStepTargetDate,
    getNextRelevantLifecycleStep,
    targetDateLabelIconDateString,
} from '@/components/automations/automationHelperComponents';
import { DatetimeFormat, formatDateWithTimezone } from '@/timeHelpers';
import React, { useMemo } from 'react';
import { dayjs } from '@/extendedDayjs';
import { RunwayUpdatable, tryUpdatable, UpdatableRequestDataType, UpdatableResourceIdsType } from '@/actions/common';
import { StepRunUpdatable } from '@/actions/stepRun';
import { StepConfigUpdatable } from '@/actions/stepConfig';
import { WorkflowUpdatable } from '@/actions/workflow';
import { OrgUpdatable } from '@/actions/organization';

export function useOrg(props: OrgComponentProps): OrganizationEntity {
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));

    return org;
}

export function useApp(props: AppComponentProps): AppEntity {
    const app = useSelector((state: RootState) => selectApp(state, props.appId));

    return app;
}

export function useAppSettings(props: AppComponentProps): AppSettings {
    const app = useSelector((state: RootState) => selectApp(state, props.appId));

    return app?.appSettings;
}

export function useAppPlatform(props: AppComponentProps): AppPlatform {
    const appSettings = useSelector((state: RootState) => selectApp(state, props.appId).appSettings);

    return appSettings.platform;
}

export function useRelease(props: ReleaseComponentProps): Release {
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId)) as Release;

    if (release.displayableNextVersion) {
        return release;
    }

    return null;
}

export function useReleaseStep(props: ReleaseStepComponentProps): ReleaseStep {
    const releaseStep = useSelector((state: RootState) =>
        getReleaseStepFromState(props.releaseStepType, props.releaseId, state),
    );

    return releaseStep;
}

export function useIsLoading(type: Updatable | string, id?: number | string): boolean {
    const isLoading = useSelector((state: RootState) => getIsLoading(type, state, id));

    return isLoading;
}

export function useIsLoadingForCollections(type: Updatable | string, ids: string[]): Record<string, boolean> {
    const memoizedObject = useMemo(() => {
        return ids.reduce(
            (acc, id) => ({
                ...acc,
                [id]: null,
            }),
            {},
        );
    }, [JSON.stringify(ids)]);

    return useSelector((state: RootState) => {
        for (const id in memoizedObject) {
            memoizedObject[id] = getIsLoading(type, state, id);
        }
        return memoizedObject;
    });
}

// [call, isLoading, error, paginationData]
type GetEntityV2HookReturnType<T extends FetchableEntityTypeV2> = [
    (data: TryFetchEntityActionData<T>, paginationData?: CommonSagaActionsData['paginationData']) => void,
    boolean,
    string,
    CommonSagaActionsData['paginationData'],
];

export function useGetEntityV2<T extends FetchableEntityTypeV2>(type: T, id: string): GetEntityV2HookReturnType<T> {
    const dispatch = useDispatch();
    const isLoading = useIsLoading(type, id);
    const error = useError(type, id);

    const call: GetEntityV2HookReturnType<T>[0] = (data, paginationData) => {
        dispatch(tryFetchEntityV2(type, data, id, paginationData));
    };

    const paginationData = useSelector((state: RootState) => selectPaginationData(state, type, id));

    return [call, isLoading, error, paginationData];
}

// [call, isLoading, responseType, error]
type CallUpdatableHookReturnType<T extends RunwayUpdatable> = [
    (data: UpdatableRequestDataType<T>) => void,
    boolean,
    Nullable<NonErrorRunwayResponseTypes>,
    string,
];

export function useCallUpdatable<T extends RunwayUpdatable>(
    type: T,
    resourceIds: UpdatableResourceIdsType<T>,
    id?: string,
): CallUpdatableHookReturnType<T> {
    if (!resourceIds) {
        return [null, false, null, null];
    }

    let defaultId = resourceIds.orgId;

    if (Object.values(StepRunUpdatable).includes(type as StepRunUpdatable)) {
        defaultId = (resourceIds as StepRunEntityRequest).stepRunId;
    } else if (Object.values(StepConfigUpdatable).includes(type as StepConfigUpdatable)) {
        defaultId = (resourceIds as StepConfigEntityRequest).stepConfigId;
    } else if (Object.values(WorkflowUpdatable).includes(type as WorkflowUpdatable)) {
        defaultId = (resourceIds as WorkflowEntityRequest).workflowId;
    } else if (Object.values(OrgUpdatable).includes(type as OrgUpdatable)) {
        defaultId = (resourceIds as OrgEntityRequest).orgId;
    }
    const finalId = id ?? defaultId;

    const dispatch = useDispatch();
    const isLoading = useIsLoading(type, finalId);
    const responseType = useGetResponseType(type, finalId);
    const error = useError(type, finalId);

    const call: CallUpdatableHookReturnType<T>[0] = (data) => {
        dispatch(tryUpdatable(type, resourceIds, data, finalId));
    };

    return useMemo(() => [call, isLoading, responseType, error], [call, isLoading, responseType, error]);
}

type CallUpdatableForCollectionsHookReturnType<T extends RunwayUpdatable> = [
    Record<string, (data: UpdatableRequestDataType<T>) => void>,
    Record<string, boolean>,
    Record<string, Nullable<NonErrorRunwayResponseTypes>>,
    Record<string, string>,
];

export function useCallUpdatableForCollections<T extends RunwayUpdatable>(
    type: T,
    resourcesIds: Record<string, UpdatableResourceIdsType<T>>,
    ids: string[],
): CallUpdatableForCollectionsHookReturnType<T> {
    if (!resourcesIds) {
        return [{}, ids.reduce((acc, id) => ({ ...acc, [id]: false }), {}), {}, {}];
    }

    const dispatch = useDispatch();
    const isLoadingCollection = useIsLoadingForCollections(type, ids);
    const responseTypeCollection = useGetResponseTypeForCollections(type, ids);
    const errorCollection = useErrorForCollections(type, ids);

    const callCollection = useMemo(
        () =>
            ids.reduce<Record<string, (data: UpdatableRequestDataType<T>) => void>>((acc, id) => {
                acc[id] = (data: UpdatableRequestDataType<T>) => {
                    dispatch(tryUpdatable(type, resourcesIds[id], data, id));
                };
                return acc;
            }, {}),
        [JSON.stringify(ids)],
    );

    return useMemo(
        () => [callCollection, isLoadingCollection, responseTypeCollection, errorCollection],
        [callCollection, isLoadingCollection, responseTypeCollection, errorCollection],
    );
}

export function useGetEntity(
    type: FetchablePaginatedEntityType | FetchableEntityType,
    id?: number | string,
): [(data: PaginatedEntitiesRequest | FetchableEntityType, limit?: number, offset?: number) => void, boolean, string] {
    const dispatch = useDispatch();

    const isLoading = useIsLoading(type, id);
    const error = useError(type, id);

    const isPaginatedUpdatable = Object.keys(FetchablePaginatedEntityType).includes(type);

    const callUpdatable = (data, limit, offset) => {
        dispatch(
            isPaginatedUpdatable ?
                tryFetchPaginatedEntities(type as FetchablePaginatedEntityType, data, limit, offset, id)
            :   tryFetchEntity(type as FetchableEntityType, data, id),
        );
    };

    return [callUpdatable, isLoading, error];
}

export function useWillUpdate(type: Updatable | string, id?: number | string): boolean {
    const willUpdate = useSelector((state: RootState) => getWillUpdate(type, state, id));

    return willUpdate;
}

export function useUpdated(type: Updatable | string, id?: number | string): boolean {
    const updated = useSelector((state: RootState) => getUpdated(type, state, id));

    return updated;
}

export function useGetResponseType(
    type: Updatable | string,
    id?: number | string,
): Nullable<NonErrorRunwayResponseTypes> {
    const updated = useSelector((state: RootState) => getResponseType(type, state, id));

    return updated;
}

export function useGetResponseTypeForCollections(
    type: Updatable | string,
    ids: string[],
): Record<string, Nullable<NonErrorRunwayResponseTypes>> {
    const memoizedObject = useMemo(() => {
        return ids.reduce(
            (acc, id) => ({
                ...acc,
                [id]: null,
            }),
            {},
        );
    }, [JSON.stringify(ids)]);

    return useSelector((state: RootState) => {
        for (const id in memoizedObject) {
            memoizedObject[id] = getResponseType(type, state, id);
        }

        return memoizedObject;
    });
}

export function useError(type: Updatable | string, id?: number | string): string {
    const error = useSelector((state: RootState) => getError(type, state, id));

    return error;
}

export function useErrorForCollections(type: Updatable | string, ids: string[]): Record<string, string> {
    const memoizedObject = useMemo(() => {
        return ids.reduce(
            (acc, id) => ({
                ...acc,
                [id]: null,
            }),
            {},
        );
    }, [JSON.stringify(ids)]);

    return useSelector((state: RootState) => {
        for (const id in memoizedObject) {
            memoizedObject[id] = getError(type, state, id);
        }

        return memoizedObject;
    });
}

export const useTestingTrackFromGoogleBetaIntegration = (appId: string): string => {
    const appSettings = useSelector((state: RootState) => selectApp(state, appId).appSettings);

    const googleBetaIntegration = appSettings.integrations.find(
        (integration) => integration.id === IntegrationType.GoogleBeta,
    )?.data as GoogleBetaIntegrationData;

    return googleBetaIntegration?.testingTrack;
};

export const useReleaseLifecycleStepDisplayInfo = (
    step: LifecycleStep,
    releaseId: string,
    appId: string,
    shortDate = false,
    releaseSummary?: NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary,
) => {
    let releaseOrReleaseSummary: Release | NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary =
        useSelector((state: RootState) => selectRelease(state, releaseId));

    if (!releaseOrReleaseSummary) {
        releaseOrReleaseSummary = releaseSummary;
    }

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const cadenceTimezone = useSelector((state: RootState) => selectCadenceTimezone(state, appId));
    const targetDate = getLifecycleStepTargetDate(step, releaseOrReleaseSummary);

    const getTimeFormatForDate = (date: dayjs.Dayjs) => {
        const format = date.minute() !== 0 ? 'shortHourOnly' : 'hourOnly';
        return formatDateWithTimezone(date, format, cadenceTimezone, use24HourTimeFormat);
    };

    const targetTime = targetDate.isValid() && getTimeFormatForDate(targetDate);

    const targetDay =
        targetDate.isValid() &&
        targetDate.format(shortDate ? DatetimeFormat.ShortDayMonthDate : DatetimeFormat.DayMonthDate);

    const isComplete = getLifecycleStepCompleted(step, releaseOrReleaseSummary);
    const completedAt = getLifecycleStepCompletedDate(step, releaseOrReleaseSummary);

    const completedDate = dayjs(completedAt);
    const completedTime = completedDate?.isValid() ? getTimeFormatForDate(completedDate) : null;
    const completedDay =
        completedDate?.isValid() ?
            completedDate.format(shortDate ? DatetimeFormat.ShortDayMonthDate : DatetimeFormat.DayMonthDate)
        :   null;

    //if complete, check to see if it was completed automatically
    // 1. is automation enabled?
    // 2. was automation successful?
    // 3. Does the completedDate match the target date?

    const automationRun = findLifecycleAutomationRun(step, releaseOrReleaseSummary.lifecycleAutomations);
    const automationEnabled =
        automationRun && !automationIsInactive(automationRun) && targetDate?.isSame(automationRun.scheduledAt, 'day');
    const automation = automationEnabled ? automationRun : null;
    const automationSuccessful = automation && automationIsCompleted(automation) && completedDate === targetDate;

    //if completed automatically, see if it matches the cadence target
    // 1. check if autoScheduleFromCadence is true
    // => if true, show cadence icon

    const wasScheduledFromCadence = automationSuccessful && automation.autoScheduledFromCadence;

    //if not complete, check to see if its scheduled
    // 1. is automation enabled?
    // 2. is target date the same as automation scheduled date?

    const scheduledDate = automationEnabled ? dayjs(automation.scheduledAt) : null;
    const scheduledDay = scheduledDate?.format(
        shortDate ? DatetimeFormat.ShortDayMonthDate : DatetimeFormat.DayMonthDate,
    );
    const scheduledTime = scheduledDate ? getTimeFormatForDate(scheduledDate) : null;
    const automationScheduled = automationEnabled && scheduledDate === targetDate;

    //if scheduled, see if it's from cadence
    // 1. check if autoScheduleFromCadence is true
    // => if true show cadence icon
    //if not scheduled, see if there is a target date

    const isScheduledFromCadence = automationScheduled && automation.autoScheduledFromCadence;
    const showCadenceIcon = isScheduledFromCadence || wasScheduledFromCadence;

    let automationIcon: React.ReactNode = null;
    if (automationEnabled && targetDate) {
        const { icon } = targetDateLabelIconDateString(automation, targetDate);
        automationIcon = icon;
    }

    const displayText = getLifecycleStepDisplayText(step, isComplete, automationSuccessful);

    const emptyTime = '-- : --';
    const emptyDate = shortDate ? '--' : '-- / -- / --';

    const displayTime =
        isComplete ? (completedTime ?? emptyTime)
        : automationEnabled ? scheduledTime
        : targetDate.isValid() ? targetTime
        : emptyTime;

    const displayDate =
        isComplete ? (completedDay ?? emptyDate)
        : automationEnabled ? scheduledDay
        : targetDate.isValid() ? targetDay
        : emptyDate;

    const hasDate = completedDate.isValid() || automationEnabled || targetDate.isValid();

    return {
        automationIcon,
        showCadenceIcon,
        isComplete,
        automationEnabled,
        targetDate,
        displayText,
        hasDate,
        displayTime,
        displayDate,
        isNextRelevantStep: getNextRelevantLifecycleStep(releaseOrReleaseSummary) === step,
    };
};
